'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'

// =============================================================================
// Types
// =============================================================================

export type TagSeverity =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warn'
  | 'danger'
  | 'contrast'

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Tag content */
  children: React.ReactNode
  /** Severity/color variant */
  severity?: TagSeverity
  /** Use pill shape (12px radius) instead of standard (6px radius) */
  rounded?: boolean
  /** Icon to display before the text */
  icon?: React.ReactNode
}

// =============================================================================
// Styles - Using design system color primitives
// =============================================================================

const severityStyles: Record<TagSeverity, string> = {
  primary: 'bg-brand-50 text-brand-700',
  secondary: 'bg-slate-100 text-slate-600',
  success: 'bg-green-50 text-green-700',
  info: 'bg-sky-50 text-sky-700',
  warn: 'bg-orange-50 text-orange-700',
  danger: 'bg-red-50 text-red-700',
  contrast: 'bg-slate-950 text-white',
}

// =============================================================================
// Component
// =============================================================================

/**
 * Tag - Small label for status, categories, or metadata
 *
 * Follows the design system specifications:
 * - 7 severity variants with specific colors
 * - Standard (6px) or rounded/pill (12px) border radius
 * - Bold typography at 12px
 * - Optional leading icon
 *
 * Usage:
 * ```tsx
 * <Tag>Default</Tag>
 * <Tag severity="success">Active</Tag>
 * <Tag severity="primary" rounded>Pill Shape</Tag>
 * <Tag icon={<CircleDot className="size-[10px]" />}>With Icon</Tag>
 * ```
 */
const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
  ({ children, severity = 'secondary', rounded = false, icon, className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          // Layout
          'inline-flex items-center gap-[3.5px]',
          // Padding: 7px horizontal, 3.5px vertical
          'px-[7px] py-[3.5px]',
          // Border radius: sm (6px) default, xl (12px) for rounded/pill
          rounded ? 'rounded-xl' : 'rounded-sm',
          // Typography: Inter Bold, ~12px
          'text-xs font-bold leading-normal whitespace-nowrap',
          // Severity colors
          severityStyles[severity],
          className
        )}
        {...props}
      >
        {icon && (
          <span className="shrink-0 size-[10.5px] flex items-center justify-center">
            {icon}
          </span>
        )}
        {children}
      </span>
    )
  }
)

Tag.displayName = 'Tag'

export { Tag }
